/**
 * Kundali Page
 * Vedic Birth Chart Generator
 *
 * This page generates and displays Vedic astrology birth charts (Kundali)
 * with planetary positions, Janma Nakshatra, and AI-powered readings.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Loading from '../components/Loading';
import { getKundaliReading, isGeminiReady } from '../services/geminiService';
import { useLanguage } from '../context/LanguageContext';
import { useUserProfile } from '../context/UserProfileContext';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';

// Import styles
import './Kundali.css';

// Import constants
import { SIGNS, SIGN_ABBR } from '../data/kundaliConstants';

// Import location data
import { searchLocalCities, geocodePlaceAPI } from '../data/locationData';

// Import calculations
import {
  calculatePlanetPositions,
  calculateJanmaNakshatraRashi,
  generateChartData,
  calculateLagna,
  calculatePanchang,
  calculateKalamTimings,
  calculateLuckyInfo,
  calculateLifePredictions,
  calculateDoshaSummary,
} from '../utils/kundaliCalculations';

// Import components
import {
  KundaliChart,
  Tip,
  PlanetaryStrengthTable,
  WeeklyPlanetaryPeriod,
  HouseAnalysisTable,
  PlanetaryTable,
  DoshaSummaryCard
} from '../components/kundali';

/**
 * Generate SVG chart for PDF - South Indian Style
 */
const generateSouthIndianChartSVG = (chartData, chartTitle, ascSign) => {
  const signPositions = {
    'Pisces': { row: 0, col: 0 }, 'Aries': { row: 0, col: 1 }, 'Taurus': { row: 0, col: 2 }, 'Gemini': { row: 0, col: 3 },
    'Aquarius': { row: 1, col: 0 }, 'Cancer': { row: 1, col: 3 },
    'Capricorn': { row: 2, col: 0 }, 'Leo': { row: 2, col: 3 },
    'Sagittarius': { row: 3, col: 0 }, 'Scorpio': { row: 3, col: 1 }, 'Libra': { row: 3, col: 2 }, 'Virgo': { row: 3, col: 3 },
  };
  const cellSize = 60;
  const padding = 5;

  const getPlanetsInSign = (signName) => {
    const signs = SIGNS;
    const ascIndex = signs.indexOf(ascSign);
    const signIndex = signs.indexOf(signName);
    const house = ((signIndex - ascIndex + 12) % 12) + 1;
    return chartData[house] || [];
  };

  let cells = '';
  Object.entries(signPositions).forEach(([sign, pos]) => {
    const x = padding + pos.col * cellSize;
    const y = padding + pos.row * cellSize;
    const planets = getPlanetsInSign(sign);
    const isAsc = sign === ascSign;

    cells += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${isAsc ? '#fff8e1' : '#fff'}" stroke="#8B4513" stroke-width="1.5"/>`;
    cells += `<text x="${x + 3}" y="${y + 12}" fill="${isAsc ? '#8B4513' : '#666'}" font-size="8" font-weight="${isAsc ? 'bold' : 'normal'}">${SIGN_ABBR[sign]}${isAsc ? '(Asc)' : ''}</text>`;
    planets.forEach((planet, idx) => {
      cells += `<text x="${x + 8 + (idx % 2) * 28}" y="${y + 28 + Math.floor(idx / 2) * 14}" fill="#333" font-size="10" font-weight="bold">${planet.abbr}</text>`;
    });
  });

  // Center box
  cells += `<rect x="${padding + cellSize}" y="${padding + cellSize}" width="${cellSize * 2}" height="${cellSize * 2}" fill="#f5f5f5" stroke="#8B4513" stroke-width="1"/>`;
  cells += `<text x="125" y="120" text-anchor="middle" fill="#8B4513" font-size="12" font-weight="bold">${chartTitle.includes('Navamsa') ? 'D9' : 'D1'}</text>`;
  cells += `<text x="125" y="138" text-anchor="middle" fill="#666" font-size="10">${chartTitle.includes('Navamsa') ? 'Navamsa' : 'Rashi'}</text>`;

  return `<svg viewBox="0 0 250 250" width="250" height="250" style="background: white; border: 1px solid #ddd; border-radius: 8px;">${cells}</svg>`;
};

/**
 * Generate SVG chart for PDF - North Indian Style
 */
const generateNorthIndianChartSVG = (chartData, chartTitle, ascSign) => {
  const signs = SIGNS;
  const size = 250;
  const center = size / 2;
  const outerSize = 110;
  const innerSize = 55;

  const getSignForHouse = (houseNum) => {
    const ascIndex = signs.indexOf(ascSign);
    return signs[(ascIndex + houseNum - 1) % 12];
  };

  const housePolygons = [
    { house: 1, points: `${center},10 ${center - outerSize},${center} ${center},${center} ${center + outerSize},${center}`, textX: center, textY: 55, planetsX: center - 15, planetsY: 75 },
    { house: 12, points: `${center},10 ${center - outerSize},${center} ${center - innerSize},${center - innerSize}`, textX: center - 60, textY: 75, planetsX: center - 70, planetsY: 95 },
    { house: 2, points: `${center},10 ${center + innerSize},${center - innerSize} ${center + outerSize},${center}`, textX: center + 60, textY: 75, planetsX: center + 50, planetsY: 95 },
    { house: 11, points: `10,10 ${center},10 ${center - innerSize},${center - innerSize} 10,${center}`, textX: 40, textY: center - 40, planetsX: 28, planetsY: center - 20 },
    { house: 3, points: `${center},10 ${size - 10},10 ${size - 10},${center} ${center + innerSize},${center - innerSize}`, textX: size - 40, textY: center - 40, planetsX: size - 52, planetsY: center - 20 },
    { house: 10, points: `10,${center} ${center - innerSize},${center - innerSize} ${center - innerSize},${center + innerSize}`, textX: 40, textY: center, planetsX: 28, planetsY: center + 20 },
    { house: 4, points: `${size - 10},${center} ${center + innerSize},${center - innerSize} ${center + innerSize},${center + innerSize}`, textX: size - 40, textY: center, planetsX: size - 52, planetsY: center + 20 },
    { house: 9, points: `10,${center} ${center - innerSize},${center + innerSize} 10,${size - 10}`, textX: 40, textY: center + 45, planetsX: 28, planetsY: center + 65 },
    { house: 5, points: `${size - 10},${center} ${center + innerSize},${center + innerSize} ${size - 10},${size - 10}`, textX: size - 40, textY: center + 45, planetsX: size - 52, planetsY: center + 65 },
    { house: 8, points: `${center},${size - 10} ${center - outerSize},${center} ${center - innerSize},${center + innerSize}`, textX: center - 60, textY: size - 70, planetsX: center - 70, planetsY: size - 50 },
    { house: 6, points: `${center},${size - 10} ${center + outerSize},${center} ${center + innerSize},${center + innerSize}`, textX: center + 60, textY: size - 70, planetsX: center + 50, planetsY: size - 50 },
    { house: 7, points: `${center},${size - 10} ${center - outerSize},${center} ${center},${center} ${center + outerSize},${center}`, textX: center, textY: size - 45, planetsX: center - 15, planetsY: size - 25 },
  ];

  let svgContent = `<rect x="10" y="10" width="${size - 20}" height="${size - 20}" fill="white" stroke="#8B4513" stroke-width="2"/>`;
  svgContent += `<line x1="10" y1="10" x2="${size - 10}" y2="${size - 10}" stroke="#8B4513" stroke-width="1.5"/>`;
  svgContent += `<line x1="${size - 10}" y1="10" x2="10" y2="${size - 10}" stroke="#8B4513" stroke-width="1.5"/>`;
  svgContent += `<polygon points="${center - innerSize},${center - innerSize} ${center + innerSize},${center - innerSize} ${center + innerSize},${center + innerSize} ${center - innerSize},${center + innerSize}" fill="#f5f5f5" stroke="#8B4513" stroke-width="1.5"/>`;

  housePolygons.forEach(({ house, points, textX, textY, planetsX, planetsY }) => {
    const sign = getSignForHouse(house);
    const planets = chartData[house] || [];
    const isLagna = house === 1;

    if (isLagna) {
      svgContent += `<polygon points="${points}" fill="#fff8e1" stroke="none"/>`;
    }
    svgContent += `<text x="${textX}" y="${textY}" text-anchor="middle" fill="${isLagna ? '#8B4513' : '#666'}" font-size="8" font-weight="${isLagna ? 'bold' : 'normal'}">${SIGN_ABBR[sign]}${isLagna ? '(Asc)' : ''}</text>`;
    planets.forEach((planet, idx) => {
      svgContent += `<text x="${planetsX + (idx % 2) * 22}" y="${planetsY + Math.floor(idx / 2) * 12}" text-anchor="middle" fill="#333" font-size="9" font-weight="bold">${planet.abbr}</text>`;
    });
  });

  svgContent += `<rect x="${center - innerSize}" y="${center - innerSize}" width="${innerSize * 2}" height="${innerSize * 2}" fill="#f5f5f5" stroke="none"/>`;
  svgContent += `<text x="${center}" y="${center - 5}" text-anchor="middle" fill="#8B4513" font-size="12" font-weight="bold">${chartTitle.includes('Navamsa') ? 'D9' : 'D1'}</text>`;
  svgContent += `<text x="${center}" y="${center + 12}" text-anchor="middle" fill="#666" font-size="10">${chartTitle.includes('Navamsa') ? 'Navamsa' : 'Rashi'}</text>`;

  return `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" style="background: white; border: 1px solid #ddd; border-radius: 8px;">${svgContent}</svg>`;
};

/**
 * Main Kundali Component
 */
export default function Kundali() {
  const [form, setForm] = useState({
    name: '',
    dob: '',
    timeOfBirth: '',
    placeOfBirth: '',
    gender: '',
    latitude: '',
    longitude: '',
    timezone: '5.5',
  });
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartGenerated, setChartGenerated] = useState(false);
  const [chartStyle, setChartStyle] = useState('south'); // 'south' or 'north'
  const [saveSuccess, setSaveSuccess] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [locationResolved, setLocationResolved] = useState('');
  const { language, t } = useLanguage();
  const { saveReading } = useUserProfile();
  const { user } = useAuth();
  const reportRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Calculate planetary positions (using user-specified timezone)
  const planetPositions = useMemo(() => {
    if (!form.dob) return [];
    const tz = parseFloat(form.timezone) || 5.5;
    return calculatePlanetPositions(form.dob, form.timeOfBirth, tz);
  }, [form.dob, form.timeOfBirth, form.timezone]);

  // Calculate Lagna (Ascendant) using proper LST + lat/lng formula
  const lagnaResult = useMemo(() => {
    if (!form.dob) return null;

    const date = new Date(form.dob);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    let hour = 6, minute = 0;
    if (form.timeOfBirth) {
      [hour, minute] = form.timeOfBirth.split(':').map(Number);
    }

    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    const tz = parseFloat(form.timezone) || 5.5;

    // If we have coordinates, use proper astronomical calculation
    if (!isNaN(lat) && !isNaN(lng)) {
      return calculateLagna(year, month, day, hour, minute, lat, lng, tz);
    }

    // Fallback: default to Ujjain (center of India, traditional reference point)
    return calculateLagna(year, month, day, hour, minute, 23.1765, 75.7885, tz);
  }, [form.dob, form.timeOfBirth, form.latitude, form.longitude, form.timezone]);

  const ascendantSign = lagnaResult ? lagnaResult.sign : 'Aries';

  // Calculate Janma Nakshatra and Rashi from Moon position
  const janmaDetails = useMemo(() => {
    if (planetPositions.length === 0) return null;
    const moon = planetPositions.find(p => p.name === 'Moon');
    if (!moon) return null;
    return calculateJanmaNakshatraRashi(moon.longitude);
  }, [planetPositions]);

  // Calculate Panchang (Tithi, Yoga, Karana, Vara) for birth time
  const panchangDetails = useMemo(() => {
    if (!form.dob) return null;
    const tz = parseFloat(form.timezone) || 5.5;
    return calculatePanchang(form.dob, form.timeOfBirth, tz);
  }, [form.dob, form.timeOfBirth, form.timezone]);

  // Calculate Rahu Kalam, Yamagandam, Gulika Kalam for birth day
  const kalamTimings = useMemo(() => {
    if (!form.dob) return null;
    const lat = parseFloat(form.latitude) || 13.0827;
    return calculateKalamTimings(form.dob, lat);
  }, [form.dob, form.latitude]);

  // Calculate Lucky information
  const luckyInfo = useMemo(() => {
    if (!janmaDetails) return null;
    return calculateLuckyInfo(janmaDetails, lagnaResult?.signIndex ?? 0);
  }, [janmaDetails, lagnaResult]);

  // Calculate Life Predictions
  const lifePredictions = useMemo(() => {
    if (!planetPositions.length || !janmaDetails) return null;
    return calculateLifePredictions(planetPositions, lagnaResult?.signIndex ?? 0, janmaDetails);
  }, [planetPositions, lagnaResult, janmaDetails]);

  // Calculate Dosha Summary
  const doshaSummary = useMemo(() => {
    if (!planetPositions.length) return null;
    return calculateDoshaSummary(planetPositions, lagnaResult?.signIndex ?? 0, janmaDetails);
  }, [planetPositions, lagnaResult, janmaDetails]);

  // Generate chart data
  const lagnaChart = useMemo(() => {
    if (planetPositions.length === 0) return {};
    return generateChartData(planetPositions, ascendantSign);
  }, [planetPositions, ascendantSign]);

  // Generate Navamsa chart (D9)
  const { navamsaChart, navamsaAscendant } = useMemo(() => {
    if (planetPositions.length === 0) return { navamsaChart: {}, navamsaAscendant: 'Aries' };

    // Calculate Navamsa positions
    const navamsaPlanets = planetPositions.map((planet) => {
      const navamsaLong = (planet.longitude * 9) % 360;
      const signIndex = Math.floor(navamsaLong / 30);
      return {
        ...planet,
        sign: SIGNS[signIndex],
        degree: Math.floor(navamsaLong % 30),
      };
    });

    // Get Navamsa ascendant from Lagna's actual sidereal longitude
    const ascSiderealLong = lagnaResult ? lagnaResult.siderealLongitude : SIGNS.indexOf(ascendantSign) * 30;
    const navAscLong = (ascSiderealLong * 9) % 360;
    const navAscSign = SIGNS[Math.floor(navAscLong / 30) % 12];

    return {
      navamsaChart: generateChartData(navamsaPlanets, navAscSign),
      navamsaAscendant: navAscSign
    };
  }, [planetPositions, ascendantSign, lagnaResult]);

  // Correct house numbers based on actual Lagna sign
  const planetsWithHouses = useMemo(() => {
    if (!planetPositions.length) return [];
    const ascIndex = SIGNS.indexOf(ascendantSign);
    return planetPositions.map(p => ({
      ...p,
      house: ((SIGNS.indexOf(p.sign) - ascIndex + 12) % 12) + 1
    }));
  }, [planetPositions, ascendantSign]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'dob' || name === 'timeOfBirth') {
      setChartGenerated(false);
    }
    if (name === 'latitude' || name === 'longitude' || name === 'timezone') {
      setChartGenerated(false);
    }
  };

  // Handle place of birth input with local city autocomplete
  const handlePlaceChange = useCallback((e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, placeOfBirth: value }));
    setChartGenerated(false);

    // Search local city database for instant suggestions
    const localResults = searchLocalCities(value);
    setLocationSuggestions(localResults);
  }, []);

  // Select a location suggestion
  const handleSelectSuggestion = useCallback((city) => {
    setForm(prev => ({
      ...prev,
      placeOfBirth: city.name,
      latitude: String(city.lat),
      longitude: String(city.lng),
      timezone: String(city.tz),
    }));
    setLocationSuggestions([]);
    setLocationResolved(city.name);
    setChartGenerated(false);
  }, []);

  // Geocode using Nominatim API (for places not in local DB)
  const handleGeocode = useCallback(async () => {
    if (!form.placeOfBirth || form.placeOfBirth.length < 2) return;

    // First check local DB
    const localResults = searchLocalCities(form.placeOfBirth);
    if (localResults.length === 1) {
      handleSelectSuggestion(localResults[0]);
      return;
    }

    setGeocoding(true);
    try {
      const results = await geocodePlaceAPI(form.placeOfBirth);
      if (results.length > 0) {
        if (results.length === 1) {
          handleSelectSuggestion(results[0]);
        } else {
          setLocationSuggestions(results);
        }
      } else {
        setError('Could not find location. Please enter latitude/longitude manually.');
      }
    } catch (err) {
      setError('Location search failed. Please enter latitude/longitude manually.');
    } finally {
      setGeocoding(false);
    }
  }, [form.placeOfBirth, handleSelectSuggestion]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setLocationSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGenerateChart = (e) => {
    e.preventDefault();
    if (!form.dob) {
      setError(t('fillDateOfBirth'));
      return;
    }
    setChartGenerated(true);
    setError('');

    // Fire-and-forget: log this generation to the backend
    const token = localStorage.getItem('astroa2z_token');
    const logPayload = {
      name:             form.name || null,
      date_of_birth:    form.dob || null,
      time_of_birth:    form.timeOfBirth || null,
      place_of_birth:   form.placeOfBirth || null,
      latitude:         form.latitude !== '' ? form.latitude : null,
      longitude:        form.longitude !== '' ? form.longitude : null,
      timezone:         form.timezone || null,
      gender:           form.gender || null,
      username:         user ? (user.name || user.email || null) : null,
      screen_width:     window.screen?.width || null,
      screen_height:    window.screen?.height || null,
      browser_language: navigator.language || null,
      referrer:         document.referrer || null,
    };
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    fetch(`${API_BASE}/api/kundali/log`, {
      method:  'POST',
      headers,
      body:    JSON.stringify(logPayload),
    }).catch(() => {/* non-critical — ignore network errors */});
  };

  const handleGetReading = async () => {
    if (!isGeminiReady()) {
      setError(t('apiKeyError'));
      return;
    }

    setLoading(true);
    setError('');
    setReading('');

    try {
      const result = await getKundaliReading(
        form.name || 'Native',
        form.dob,
        form.timeOfBirth || 'Unknown',
        form.placeOfBirth || 'Unknown',
        form.gender || 'Unknown',
        ascendantSign,
        planetsWithHouses,
        language
      );
      setReading(result);
    } catch (err) {
      setError(err.message || 'Failed to generate Kundali reading.');
    } finally {
      setLoading(false);
    }
  };

  // Save Kundali report to profile
  const handleSaveToProfile = () => {
    const kundaliData = {
      type: 'kundali',
      title: `Kundali - ${form.name || t('native')}`,
      name: form.name || t('native'),
      dob: form.dob,
      timeOfBirth: form.timeOfBirth,
      placeOfBirth: form.placeOfBirth,
      gender: form.gender,
      ascendantSign,
      janmaDetails: janmaDetails ? {
        nakshatra: janmaDetails.nakshatra.name,
        pada: janmaDetails.pada,
        rashi: janmaDetails.rashi.name,
        rashiLord: janmaDetails.rashi.lord,
        nakshatraLord: janmaDetails.nakshatra.lord,
      } : null,
      planetPositions: planetsWithHouses.map(p => ({
        name: p.name,
        sign: p.sign,
        degree: p.degree,
        house: p.house,
        nakshatra: p.nakshatra,
        retrograde: p.retrograde,
      })),
      reading: reading,
    };

    saveReading(kundaliData);
    setSaveSuccess(t('savedToProfile') || 'Kundali saved to your profile!');
    setTimeout(() => setSaveSuccess(''), 3000);
  };

  // Generate and save as PDF
  const handleSaveAsPDF = () => {
    // Generate chart SVGs
    const southLagnaChart = generateSouthIndianChartSVG(lagnaChart, 'Lagna', ascendantSign);
    const southNavamsaChart = generateSouthIndianChartSVG(navamsaChart, 'Navamsa', navamsaAscendant);
    const northLagnaChart = generateNorthIndianChartSVG(lagnaChart, 'Lagna', ascendantSign);
    const northNavamsaChart = generateNorthIndianChartSVG(navamsaChart, 'Navamsa', navamsaAscendant);

    // Create a printable version of the report
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Kundali Report - ${form.name || 'Native'}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            color: #333;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #FFD700;
          }
          .header h1 {
            color: #8B4513;
            margin-bottom: 10px;
            font-size: 28px;
          }
          .header p { color: #666; }
          .section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          .section h2 {
            color: #8B4513;
            border-bottom: 1px solid #ddd;
            padding-bottom: 8px;
            margin-bottom: 15px;
            font-size: 18px;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }
          .info-item {
            background: #f9f9f9;
            padding: 12px;
            border-radius: 8px;
            border-left: 3px solid #FFD700;
          }
          .info-item label {
            font-weight: bold;
            color: #8B4513;
            display: block;
            margin-bottom: 4px;
            font-size: 12px;
          }
          .info-item span { font-size: 14px; }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            font-size: 13px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 10px 8px;
            text-align: left;
          }
          th {
            background: linear-gradient(135deg, #8B4513, #A0522D);
            color: white;
            font-weight: 600;
          }
          tr:nth-child(even) { background: #f9f9f9; }
          .reading-content {
            background: #f5f5f5;
            padding: 20px;
            border-radius: 10px;
            white-space: pre-wrap;
            font-size: 14px;
          }
          .charts-container { margin: 20px 0; }
          .charts-row {
            display: flex;
            justify-content: center;
            gap: 30px;
            margin-bottom: 20px;
            flex-wrap: wrap;
          }
          .chart-wrapper { text-align: center; }
          .chart-wrapper h4 {
            color: #8B4513;
            margin-bottom: 10px;
            font-size: 14px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            color: #888;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .section { page-break-inside: avoid; }
            .charts-row { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🪬 Kundali Report</h1>
          <p>${form.name || 'Native'} | ${new Date(form.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}${form.timeOfBirth ? ' | ' + form.timeOfBirth : ''}</p>
        </div>

        <div class="section">
          <h2>📋 Birth Details</h2>
          <div class="info-grid">
            <div class="info-item"><label>Name</label><span>${form.name || 'Native'}</span></div>
            <div class="info-item"><label>Date of Birth</label><span>${new Date(form.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span></div>
            <div class="info-item"><label>Time of Birth</label><span>${form.timeOfBirth || 'Not specified'}</span></div>
            <div class="info-item"><label>Place of Birth</label><span>${form.placeOfBirth || 'Not specified'}</span></div>
            <div class="info-item"><label>Lagna (Ascendant)</label><span>${ascendantSign}</span></div>
            <div class="info-item"><label>Gender</label><span>${form.gender || 'Not specified'}</span></div>
          </div>
        </div>

        ${janmaDetails ? `
        <div class="section">
          <h2>⭐ Janma Nakshatra & Rashi</h2>
          <div class="info-grid">
            <div class="info-item"><label>Janma Nakshatra</label><span>${janmaDetails.nakshatra.name} (${janmaDetails.nakshatra.nameTe})</span></div>
            <div class="info-item"><label>Pada</label><span>${janmaDetails.pada}</span></div>
            <div class="info-item"><label>Janma Rashi</label><span>${janmaDetails.rashi.name} (${janmaDetails.rashi.nameTe})</span></div>
            <div class="info-item"><label>Rashi Lord</label><span>${janmaDetails.rashi.lord}</span></div>
            <div class="info-item"><label>Nakshatra Lord</label><span>${janmaDetails.nakshatra.lord}</span></div>
            <div class="info-item"><label>Naming Syllable</label><span style="font-size: 18px; font-weight: bold; color: #8B4513;">${janmaDetails.namingSyllable}</span></div>
          </div>
        </div>
        ` : ''}

        ${panchangDetails ? `
        <div class="section">
          <h2>🗓️ Panchang at Birth</h2>
          <div class="info-grid">
            <div class="info-item"><label>Vara (Day)</label><span>${panchangDetails.vara.vedic} (${panchangDetails.vara.name})</span></div>
            <div class="info-item"><label>Tithi</label><span>${panchangDetails.tithi.name} — ${panchangDetails.tithi.paksha} Paksha (${panchangDetails.tithi.number}/15)</span></div>
            <div class="info-item"><label>Nakshatra</label><span>${panchangDetails.nakshatra.name} — Pada ${panchangDetails.nakshatra.pada}</span></div>
            <div class="info-item"><label>Yoga</label><span>${panchangDetails.yoga.name}</span></div>
            <div class="info-item"><label>Karana</label><span>${panchangDetails.karana.name}</span></div>
          </div>
        </div>
        ` : ''}

        ${kalamTimings ? `
        <div class="section">
          <h2>⏰ Inauspicious Periods on Birth Day</h2>
          <div class="info-grid">
            <div class="info-item"><label>Day</label><span>${kalamTimings.dayOfWeek} | Sunrise: ${kalamTimings.sunrise} | Sunset: ${kalamTimings.sunset}</span></div>
            <div class="info-item"><label>Rahu Kalam</label><span>${kalamTimings.rahuKalam.start} – ${kalamTimings.rahuKalam.end}</span></div>
            <div class="info-item"><label>Yamagandam</label><span>${kalamTimings.yamagandam.start} – ${kalamTimings.yamagandam.end}</span></div>
            <div class="info-item"><label>Gulika Kalam</label><span>${kalamTimings.gulikaKalam.start} – ${kalamTimings.gulikaKalam.end}</span></div>
          </div>
        </div>
        ` : ''}

        ${luckyInfo ? `
        <div class="section">
          <h2>🍀 Lucky Information</h2>
          <div class="info-grid">
            <div class="info-item"><label>Lucky Numbers</label><span>${luckyInfo.luckyNumbers.join(', ')}</span></div>
            <div class="info-item"><label>Lucky Color</label><span>${luckyInfo.luckyColor.en}</span></div>
            <div class="info-item"><label>Lucky Day</label><span>${luckyInfo.luckyDay.en}</span></div>
            <div class="info-item"><label>Lucky Metal</label><span>${luckyInfo.luckyMetal.en}</span></div>
            <div class="info-item"><label>Gemstone</label><span>${luckyInfo.gemstone.name} — ${luckyInfo.gemstone.finger}</span></div>
            <div class="info-item"><label>Compatible Nakshatras</label><span>${luckyInfo.compatibleNakshatras.slice(0, 6).join(', ')}</span></div>
          </div>
        </div>
        ` : ''}

        ${lifePredictions ? `
        <div class="section">
          <h2>🔮 Life Predictions Summary</h2>
          <div class="predictions-pdf">
            <p><strong>💍 Marriage:</strong> ${lifePredictions.marriage.en}</p>
            <p><strong>💼 Career:</strong> ${lifePredictions.career.en}</p>
            <p><strong>🏥 Health:</strong> ${lifePredictions.health.en}</p>
            <p><strong>🪐 Saturn Transit:</strong> ${lifePredictions.saturnTransit.en}</p>
            <p><strong>🌟 Jupiter Transit:</strong> ${lifePredictions.jupiterTransit.en}</p>
          </div>
          <p style="font-size: 10px; color: #666; margin-top: 10px;">⚠️ These are general predictions only. Consult an experienced astrologer for personalized advice.</p>
        </div>
        ` : ''}

        <div class="section">
          <h2>🔲 Charts - South Indian Style</h2>
          <div class="charts-container">
            <div class="charts-row">
              <div class="chart-wrapper"><h4>Lagna Chart (D1)</h4>${southLagnaChart}</div>
              <div class="chart-wrapper"><h4>Navamsa Chart (D9)</h4>${southNavamsaChart}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <h2>🔷 Charts - North Indian Style</h2>
          <div class="charts-container">
            <div class="charts-row">
              <div class="chart-wrapper"><h4>Lagna Chart (D1)</h4>${northLagnaChart}</div>
              <div class="chart-wrapper"><h4>Navamsa Chart (D9)</h4>${northNavamsaChart}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <h2>🪐 Planetary Positions</h2>
          <table>
            <thead>
              <tr><th>Planet</th><th>Sign</th><th>Degree</th><th>House</th><th>Nakshatra</th><th>Status</th></tr>
            </thead>
            <tbody>
              ${planetsWithHouses.map(p => `
                <tr>
                  <td>${p.symbol} ${p.name}</td>
                  <td>${p.sign}</td>
                  <td>${p.degree}°${p.minutes}'</td>
                  <td>${p.house}</td>
                  <td>${p.nakshatra} (${p.pada})</td>
                  <td>${p.retrograde ? '℞ Retrograde' : ''}${p.combust ? ' ☼ Combust' : ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        ${reading ? `
        <div class="section">
          <h2>✨ AI Kundali Reading</h2>
          <div class="reading-content">${reading}</div>
        </div>
        ` : ''}

        <div class="footer">
          <p>Generated by AstroA2Z | ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <p>🕉️ For guidance purposes only</p>
        </div>
      </body>
      </html>
    `;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 500);
    } else {
      setError('Please allow popups to save as PDF');
    }
  };

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('kundaliTitle')}</h2>
          <p>{t('kundaliDesc')}</p>
        </div>

        <div className="card" style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>
          <form onSubmit={handleGenerateChart}>
            <div className="form-group">
              <label>{t('fullName')}</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t('enterFullName')}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('dateOfBirth')} *</label>
                <input type="date" name="dob" value={form.dob} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>{t('timeOfBirth')}</label>
                <input type="time" name="timeOfBirth" value={form.timeOfBirth} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ position: 'relative' }} ref={suggestionsRef}>
                <label>{t('placeOfBirth')}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    name="placeOfBirth"
                    value={form.placeOfBirth}
                    onChange={handlePlaceChange}
                    onBlur={() => {
                      setTimeout(() => {
                        if (!form.latitude && form.placeOfBirth.length >= 2) {
                          handleGeocode();
                        }
                      }, 300);
                    }}
                    placeholder={t('cityCountry')}
                    autoComplete="off"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={handleGeocode}
                    disabled={geocoding || !form.placeOfBirth}
                    className="btn btn-small"
                    style={{
                      padding: '0.5rem 0.75rem',
                      fontSize: '1rem',
                      minWidth: '40px',
                      background: 'rgba(255, 215, 0, 0.2)',
                      border: '1px solid var(--accent-gold)',
                      color: 'var(--accent-gold)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                    title="Search location"
                  >
                    {geocoding ? '...' : '📍'}
                  </button>
                </div>
                {locationSuggestions.length > 0 && (
                  <div className="location-suggestions">
                    {locationSuggestions.map((city, i) => (
                      <div
                        key={`${city.name}-${i}`}
                        onClick={() => handleSelectSuggestion(city)}
                        className="suggestion-item"
                      >
                        📍 {city.name}
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                          ({city.lat.toFixed(2)}°, {city.lng.toFixed(2)}°)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {locationResolved && (
                  <div style={{ fontSize: '0.75rem', color: '#4CAF50', marginTop: '0.25rem' }}>
                    ✓ {locationResolved}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>{t('gender')}</label>
                <select name="gender" value={form.gender} onChange={handleChange}>
                  <option value="">{t('selectGender')}</option>
                  <option value="male">{t('male')}</option>
                  <option value="female">{t('female')}</option>
                  <option value="other">{t('other')}</option>
                </select>
              </div>
            </div>

            {/* Coordinates & Timezone row */}
            <div className="form-row" style={{ gap: '0.5rem' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label style={{ fontSize: '0.8rem' }}>Lat °N</label>
                <input type="number" step="0.0001" name="latitude" value={form.latitude} onChange={handleChange} placeholder="e.g. 17.38" style={{ fontSize: '0.85rem' }} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label style={{ fontSize: '0.8rem' }}>Lng °E</label>
                <input type="number" step="0.0001" name="longitude" value={form.longitude} onChange={handleChange} placeholder="e.g. 78.49" style={{ fontSize: '0.85rem' }} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label style={{ fontSize: '0.8rem' }}>Timezone (UTC±)</label>
                <input type="number" step="0.5" name="timezone" value={form.timezone} onChange={handleChange} style={{ fontSize: '0.85rem' }} />
              </div>
            </div>
            {!form.latitude && !form.longitude && (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '-0.5rem', marginBottom: '0.75rem', textAlign: 'center' }}>
                {language === 'te'
                  ? '📍 స్థలం నమోదు చేయండి లేదా అక్షాంశ/రేఖాంశ మాన్యువల్‌గా నమోదు చేయండి'
                  : '📍 Enter a place name above or type coordinates manually for accurate Lagna'}
              </p>
            )}
            <button className="btn btn-gold" type="submit" style={{ width: '100%' }}>
              {t('generateKundali')}
            </button>
          </form>
        </div>

        {error && <div className="error-message">{error}</div>}

        {chartGenerated && (
          <>
            {/* Janma Nakshatra & Rashi Info */}
            {janmaDetails && (
              <div className="janma-info-card">
                <div className="janma-header">
                  <span className="janma-symbol">{janmaDetails.nakshatra.symbol}</span>
                  <div>
                    <h3>{form.name || t('native')}</h3>
                    <p className="janma-subtitle">{t('lagna')}: {ascendantSign} {lagnaResult ? `${Math.floor(lagnaResult.degree)}°${Math.floor((lagnaResult.degree % 1) * 60)}'` : ''} <Tip tipKey="lagna" /></p>
                  </div>
                </div>

                <div className="janma-grid">
                  <div className="janma-item">
                    <span className="janma-label">{t('janmaNakshatra')} <Tip tipKey="nakshatra" /></span>
                    <span className="janma-value">{janmaDetails.nakshatra.name}</span>
                    <span className="janma-native">{janmaDetails.nakshatra.nameTe} / {janmaDetails.nakshatra.nameHi}</span>
                  </div>
                  <div className="janma-item">
                    <span className="janma-label">{t('pada')} <Tip tipKey="pada" /></span>
                    <span className="janma-value">{janmaDetails.pada}</span>
                    <span className="janma-native">{t('padaNum', { num: janmaDetails.pada })}</span>
                  </div>
                  <div className="janma-item">
                    <span className="janma-label">{t('janmaRashi')} <Tip tipKey="rashi" /></span>
                    <span className="janma-value">{janmaDetails.rashi.name}</span>
                    <span className="janma-native">{janmaDetails.rashi.nameTe} / {janmaDetails.rashi.nameHi}</span>
                  </div>
                  <div className="janma-item">
                    <span className="janma-label">{t('rashiLord')} <Tip tipKey="rashiLord" /></span>
                    <span className="janma-value">{janmaDetails.rashi.lord}</span>
                    <span className="janma-native">{t('nakshatraLord')}: {janmaDetails.nakshatra.lord}</span>
                  </div>
                </div>

                <div className="janma-extra">
                  <div className="janma-extra-item">
                    <span className="label">{t('namingSyllable')}: <Tip tipKey="namingSyllable" /></span>
                    <span className="value highlight">{janmaDetails.namingSyllable}</span>
                  </div>
                  <div className="janma-extra-item">
                    <span className="label">{t('nakshatraDeity')}:</span>
                    <span className="value">{janmaDetails.nakshatra.deity}</span>
                  </div>
                  <div className="janma-extra-item">
                    <span className="label">{t('moonDegree')}:</span>
                    <span className="value">{janmaDetails.rashi.english} {Math.floor(janmaDetails.degreeInRashi)}°{Math.floor((janmaDetails.degreeInRashi % 1) * 60)}'</span>
                  </div>
                </div>

                <div className="ayanamsa-info">
                  <span>{t('ayanamsa')} <Tip tipKey="ayanamsa" />: Lahiri (Chitrapaksha) — {lagnaResult?.ayanamsa?.toFixed(2)}°</span>
                  <span style={{ marginLeft: '1rem' }}>{t('timezone')}: UTC{parseFloat(form.timezone) >= 0 ? '+' : ''}{form.timezone}</span>
                  {lagnaResult && <span style={{ marginLeft: '1rem' }}>LST: {(lagnaResult.lst / 15).toFixed(2)}h</span>}
                </div>

                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.5rem', textAlign: 'center' }}>
                  {t('dob')}: {new Date(form.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {form.timeOfBirth && ` | ${t('time')}: ${form.timeOfBirth}`}
                </p>
              </div>
            )}

            {/* Chart Style Toggle */}
            <div className="chart-style-toggle" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{t('chartStyle')}:</span>
              <div className="toggle-buttons" style={{ display: 'flex', background: 'rgba(255, 215, 0, 0.1)', borderRadius: '25px', padding: '4px', border: '1px solid var(--accent-gold)' }}>
                <button
                  onClick={() => setChartStyle('south')}
                  style={{
                    padding: '0.5rem 1.25rem', border: 'none', borderRadius: '20px', cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: '600', transition: 'all 0.3s ease',
                    background: chartStyle === 'south' ? 'var(--accent-gold)' : 'transparent',
                    color: chartStyle === 'south' ? '#000' : 'var(--text-secondary)'
                  }}
                >
                  🔲 {t('southIndian')}
                </button>
                <button
                  onClick={() => setChartStyle('north')}
                  style={{
                    padding: '0.5rem 1.25rem', border: 'none', borderRadius: '20px', cursor: 'pointer',
                    fontSize: '0.85rem', fontWeight: '600', transition: 'all 0.3s ease',
                    background: chartStyle === 'north' ? 'var(--accent-gold)' : 'transparent',
                    color: chartStyle === 'north' ? '#000' : 'var(--text-secondary)'
                  }}
                >
                  🔷 {t('northIndian')}
                </button>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="kundali-charts-grid">
              <div className="card kundali-card">
                <KundaliChart chartData={lagnaChart} title={t('lagnaChart')} ascendantSign={ascendantSign} chartStyle={chartStyle} />
              </div>
              <div className="card kundali-card">
                <KundaliChart chartData={navamsaChart} title={t('navamsaChart')} ascendantSign={navamsaAscendant} chartStyle={chartStyle} />
              </div>
            </div>

            {/* Panchang at Birth */}
            {panchangDetails && (
              <div className="card panchang-card" style={{ maxWidth: '700px', margin: '2rem auto' }}>
                <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  🗓️ {language === 'te' ? 'జనన పంచాంగం' : 'Panchang at Birth'}
                  <Tip tipKey="panchang" />
                </h4>
                <div className="panchang-grid">
                  <div className="panchang-item">
                    <span className="panchang-label">{language === 'te' ? 'వారం' : 'Vara (Day)'}</span>
                    <span className="panchang-value">{panchangDetails.vara.vedic}</span>
                    <span className="panchang-sub">
                      {language === 'te' ? panchangDetails.vara.nameTe : panchangDetails.vara.name}
                    </span>
                  </div>
                  <div className="panchang-item">
                    <span className="panchang-label">{language === 'te' ? 'తిథి' : 'Tithi'}</span>
                    <span className="panchang-value">
                      {language === 'te' ? panchangDetails.tithi.nameTe : panchangDetails.tithi.name}
                    </span>
                    <span className="panchang-sub">
                      {language === 'te' ? panchangDetails.tithi.pakshaTe : `${panchangDetails.tithi.paksha} Paksha`} • {panchangDetails.tithi.number}/15
                    </span>
                  </div>
                  <div className="panchang-item">
                    <span className="panchang-label">{language === 'te' ? 'నక్షత్రం' : 'Nakshatra'}</span>
                    <span className="panchang-value">
                      {language === 'te' ? panchangDetails.nakshatra.nameTe : panchangDetails.nakshatra.name}
                    </span>
                    <span className="panchang-sub">
                      {language === 'te' ? `పాదం ${panchangDetails.nakshatra.pada}` : `Pada ${panchangDetails.nakshatra.pada}`} • {panchangDetails.nakshatra.lord}
                    </span>
                  </div>
                  <div className="panchang-item">
                    <span className="panchang-label">{language === 'te' ? 'యోగం' : 'Yoga'}</span>
                    <span className="panchang-value">
                      {language === 'te' ? panchangDetails.yoga.nameTe : panchangDetails.yoga.name}
                    </span>
                    <span className="panchang-sub">#{panchangDetails.yoga.number}/27</span>
                  </div>
                  <div className="panchang-item">
                    <span className="panchang-label">{language === 'te' ? 'కరణం' : 'Karana'}</span>
                    <span className="panchang-value">
                      {language === 'te' ? panchangDetails.karana.nameTe : panchangDetails.karana.name}
                    </span>
                  </div>
                </div>
                <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                  {language === 'te'
                    ? 'పంచాంగం = తిథి + వారం + నక్షత్రం + యోగం + కరణం — ఐదు అంగాలు'
                    : 'Panchang = Tithi + Vara + Nakshatra + Yoga + Karana — the five limbs of time'}
                </p>
              </div>
            )}

            {/* Rahu Kalam / Yamagandam / Gulika Kalam */}
            {kalamTimings && (
              <div className="card kalam-card" style={{ maxWidth: '700px', margin: '0 auto 2rem' }}>
                <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  ⏰ {language === 'te' ? 'జననదిన అశుభ కాలాలు' : 'Inauspicious Periods on Birth Day'}
                  <Tip tipKey="kalamTimings" />
                </h4>
                <div className="kalam-grid">
                  <div className="kalam-sunrise">
                    <span className="kalam-label">{language === 'te' ? 'సూర్యోదయం' : 'Sunrise'}</span>
                    <span className="kalam-time">{kalamTimings.sunrise}</span>
                    <span className="kalam-label" style={{ marginTop: '0.5rem' }}>{language === 'te' ? 'సూర్యాస్తమయం' : 'Sunset'}</span>
                    <span className="kalam-time">{kalamTimings.sunset}</span>
                  </div>
                  <div className="kalam-item rahu">
                    <span className="kalam-label">{language === 'te' ? kalamTimings.rahuKalam.nameTe : kalamTimings.rahuKalam.name}</span>
                    <span className="kalam-time">{kalamTimings.rahuKalam.start} – {kalamTimings.rahuKalam.end}</span>
                    <span className="kalam-desc">{language === 'te' ? kalamTimings.rahuKalam.descriptionTe : kalamTimings.rahuKalam.description}</span>
                  </div>
                  <div className="kalam-item yama">
                    <span className="kalam-label">{language === 'te' ? kalamTimings.yamagandam.nameTe : kalamTimings.yamagandam.name}</span>
                    <span className="kalam-time">{kalamTimings.yamagandam.start} – {kalamTimings.yamagandam.end}</span>
                    <span className="kalam-desc">{language === 'te' ? kalamTimings.yamagandam.descriptionTe : kalamTimings.yamagandam.description}</span>
                  </div>
                  <div className="kalam-item gulika">
                    <span className="kalam-label">{language === 'te' ? kalamTimings.gulikaKalam.nameTe : kalamTimings.gulikaKalam.name}</span>
                    <span className="kalam-time">{kalamTimings.gulikaKalam.start} – {kalamTimings.gulikaKalam.end}</span>
                    <span className="kalam-desc">{language === 'te' ? kalamTimings.gulikaKalam.descriptionTe : kalamTimings.gulikaKalam.description}</span>
                  </div>
                </div>
                <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.75rem' }}>
                  {language === 'te'
                    ? `${kalamTimings.dayOfWeekTe} — ఈ కాలాలలో ముఖ్యమైన పనులు మానుకోవాలి`
                    : `${kalamTimings.dayOfWeek} — Avoid starting important tasks during these periods`}
                </p>
              </div>
            )}

            {/* Dosha Summary Card */}
            {doshaSummary && (
              <div className="card dosha-card" style={{ maxWidth: '800px', margin: '0 auto 2rem' }}>
                <DoshaSummaryCard doshaSummary={doshaSummary} />
              </div>
            )}

            {/* Lucky Information */}
            {luckyInfo && (
              <div className="card lucky-card" style={{ maxWidth: '700px', margin: '0 auto 2rem' }}>
                <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  🍀 {language === 'te' ? 'అదృష్ట సమాచారం' : 'Lucky Information'}
                  <Tip tipKey="luckyInfo" />
                </h4>
                <div className="lucky-grid">
                  <div className="lucky-item">
                    <span className="lucky-label">{language === 'te' ? 'అదృష్ట సంఖ్యలు' : 'Lucky Numbers'}</span>
                    <span className="lucky-value lucky-numbers">{luckyInfo.luckyNumbers.join(', ')}</span>
                  </div>
                  <div className="lucky-item">
                    <span className="lucky-label">{language === 'te' ? 'అదృష్ట రంగు' : 'Lucky Color'}</span>
                    <span className="lucky-value">{language === 'te' ? luckyInfo.luckyColor.te : luckyInfo.luckyColor.en}</span>
                  </div>
                  <div className="lucky-item">
                    <span className="lucky-label">{language === 'te' ? 'అదృష్ట రోజు' : 'Lucky Day'}</span>
                    <span className="lucky-value">{language === 'te' ? luckyInfo.luckyDay.te : luckyInfo.luckyDay.en}</span>
                  </div>
                  <div className="lucky-item">
                    <span className="lucky-label">{language === 'te' ? 'అదృష్ట లోహం' : 'Lucky Metal'}</span>
                    <span className="lucky-value">{language === 'te' ? luckyInfo.luckyMetal.te : luckyInfo.luckyMetal.en}</span>
                  </div>
                  <div className="lucky-item gemstone-item">
                    <span className="lucky-label">{language === 'te' ? 'రత్నం (రాశి అధిపతి)' : 'Gemstone (Rashi Lord)'}</span>
                    <span className="lucky-value gemstone">💎 {language === 'te' ? luckyInfo.gemstone.nameTe : luckyInfo.gemstone.name}</span>
                    <span className="lucky-sub">{luckyInfo.rashiLord} — {luckyInfo.gemstone.finger}</span>
                  </div>
                </div>
                <div className="compat-section" style={{ marginTop: '1rem' }}>
                  <span className="lucky-label" style={{ display: 'block', textAlign: 'center', marginBottom: '0.5rem' }}>
                    {language === 'te' ? 'వివాహానికి అనుకూల నక్షత్రాలు' : 'Compatible Nakshatras for Marriage'}
                  </span>
                  <div className="compat-stars">
                    {luckyInfo.compatibleNakshatras.map((star, idx) => (
                      <span key={idx} className="compat-star">★ {star}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Life Predictions Summary */}
            {lifePredictions && (
              <div className="card predictions-card" style={{ maxWidth: '700px', margin: '0 auto 2rem' }}>
                <h4 style={{ color: 'var(--accent-gold)', marginBottom: '1rem', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  🔮 {language === 'te' ? 'జీవిత అంచనాలు' : 'Life Predictions Summary'}
                  <Tip tipKey="lifePredictions" />
                </h4>
                <div className="predictions-list">
                  <div className="prediction-item">
                    <div className="prediction-icon">💍</div>
                    <div className="prediction-content">
                      <span className="prediction-label">{language === 'te' ? 'వివాహం' : 'Marriage'}</span>
                      <span className="prediction-text">{language === 'te' ? lifePredictions.marriage.te : lifePredictions.marriage.en}</span>
                    </div>
                  </div>
                  <div className="prediction-item">
                    <div className="prediction-icon">💼</div>
                    <div className="prediction-content">
                      <span className="prediction-label">{language === 'te' ? 'వృత్తి' : 'Career'}</span>
                      <span className="prediction-text">{language === 'te' ? lifePredictions.career.te : lifePredictions.career.en}</span>
                    </div>
                  </div>
                  <div className="prediction-item">
                    <div className="prediction-icon">🏥</div>
                    <div className="prediction-content">
                      <span className="prediction-label">{language === 'te' ? 'ఆరోగ్యం' : 'Health'}</span>
                      <span className="prediction-text">{language === 'te' ? lifePredictions.health.te : lifePredictions.health.en}</span>
                    </div>
                  </div>
                  <div className="prediction-item">
                    <div className="prediction-icon">🪐</div>
                    <div className="prediction-content">
                      <span className="prediction-label">{language === 'te' ? 'శని సంచారం' : 'Saturn Transit'}</span>
                      <span className="prediction-text">{language === 'te' ? lifePredictions.saturnTransit.te : lifePredictions.saturnTransit.en}</span>
                    </div>
                  </div>
                  <div className="prediction-item">
                    <div className="prediction-icon">🌟</div>
                    <div className="prediction-content">
                      <span className="prediction-label">{language === 'te' ? 'బృహస్పతి సంచారం' : 'Jupiter Transit'}</span>
                      <span className="prediction-text">{language === 'te' ? lifePredictions.jupiterTransit.te : lifePredictions.jupiterTransit.en}</span>
                    </div>
                  </div>
                </div>
                <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '1rem', fontStyle: 'italic' }}>
                  {language === 'te'
                    ? '⚠️ ఇవి సాధారణ అంచనాలు మాత్రమే. వ్యక్తిగత సలహా కోసం అనుభవజ్ఞుడైన జ్యోతిష్కుని సంప్రదించండి.'
                    : '⚠️ These are general predictions only. Consult an experienced astrologer for personalized advice.'}
                </p>
              </div>
            )}

            {/* Planetary Positions Table */}
            <div className="card" style={{ marginTop: '2rem', overflowX: 'auto' }}>
              <PlanetaryTable planets={planetsWithHouses} />
            </div>

            {/* Planetary Strength Analysis */}
            <div className="card" style={{ marginTop: '2rem', overflowX: 'auto' }}>
              <PlanetaryStrengthTable planets={planetsWithHouses} lagnaSignIndex={lagnaResult?.signIndex ?? 0} />
            </div>

            {/* Weekly Planetary Periods */}
            <div className="card" style={{ marginTop: '2rem' }}>
              <WeeklyPlanetaryPeriod />
            </div>

            {/* House Analysis with Remedies */}
            <div className="card" style={{ marginTop: '2rem', overflowX: 'auto' }}>
              <HouseAnalysisTable planets={planetsWithHouses} ascendantSign={ascendantSign} />
            </div>

            {/* Get AI Reading Button */}
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button className="btn btn-primary" onClick={handleGetReading} disabled={loading}>
                {loading ? t('analyzingKundali') : t('getKundaliReading')}
              </button>
            </div>

            {/* Save Options */}
            <div className="save-options" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
              <button className="btn btn-secondary" onClick={handleSaveToProfile} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem' }}>
                💾 {t('saveToProfile') || 'Save to Profile'}
              </button>
              <button className="btn btn-secondary" onClick={handleSaveAsPDF} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem' }}>
                📄 {t('saveAsPDF') || 'Save as PDF'}
              </button>
            </div>

            {saveSuccess && (
              <div style={{ textAlign: 'center', marginTop: '1rem', padding: '0.75rem 1rem', background: 'rgba(76, 175, 80, 0.2)', border: '1px solid #4CAF50', borderRadius: '8px', color: '#4CAF50' }}>
                ✅ {saveSuccess}
              </div>
            )}
          </>
        )}

        {loading && <Loading message={t('loadingKundali')} />}

        {reading && (
          <div className="result-container" ref={reportRef}>
            <div className="result-card">
              <h3>{t('kundaliResult', { name: form.name || t('native') })}</h3>
              <div className="content">{reading}</div>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="card" style={{ marginTop: '2rem' }}>
          <h3 style={{ color: 'var(--accent-gold)', marginBottom: '1rem' }}>
            {t('aboutKundali')}
          </h3>
          <div className="kundali-info-grid">
            <div className="info-item">
              <h4>🪬 {t('lagnaChart')}</h4>
              <p>{t('lagnaChartDesc')}</p>
            </div>
            <div className="info-item">
              <h4>💫 {t('navamsaChart')}</h4>
              <p>{t('navamsaChartDesc')}</p>
            </div>
            <div className="info-item">
              <h4>🪐 {t('nineGrahas')}</h4>
              <p>{t('nineGrahasDesc')}</p>
            </div>
            <div className="info-item">
              <h4>⭐ {t('twelveHouses')}</h4>
              <p>{t('twelveHousesDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
